import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../components/Translation';

type Course = {
  key: string;
  image: string;
};

type Partner = {
  name: string;
  logoSrc?: string;
  logoClassName?: string;
  color: string;
};

const focusAreas = [
  { key: 'laboratory', icon: 'chip' },
  { key: 'equipment', icon: 'equipment' },
  { key: 'integrated_circuits', icon: 'network' },
];

const equipmentPhotos = [
  { src: '/img/researchLines/MayanOnChip/capabilities/robotic-arm.png', altKey: 'area_equipment_robotic_arm_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/3d-printer.png', altKey: 'area_equipment_printer_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/laser-marker.png', altKey: 'area_equipment_laser_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/inspection-microscope.png', altKey: 'area_equipment_microscope_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/oscilloscope.png', altKey: 'area_equipment_oscilloscope_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/bench-power-supply.png', altKey: 'area_equipment_power_supply_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/soldering-station.png', altKey: 'area_equipment_soldering_station_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/workstation.png', altKey: 'area_equipment_workstation_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/industrial-3d-printer.png', altKey: 'area_equipment_industrial_printer_alt' },
  { src: '/img/researchLines/MayanOnChip/capabilities/pick-and-place.png', altKey: 'area_equipment_pick_and_place_alt' },
];

const programStats = [
  { key: 'global_applicants', value: 600, suffix: '+', categoryKey: 'global_stat_label' },
  { key: 'current_cohort', value: 90, suffix: '', categoryKey: 'current_semester_stat_label' },
  { key: 'certified_people', value: 220, suffix: '', categoryKey: 'program_stat_label' },
  { key: 'courses', value: 4, suffix: '', categoryKey: 'program_stat_label' },
];

const courses: Course[] = [
  { key: 'verilog_hdl', image: 'verilog-hdl.png' },
  { key: 'systemverilog', image: 'systemverilog.png' },
  { key: 'pcb_design', image: 'pcb-design-altium.png' },
  { key: 'circuit_simulation', image: 'circuit-simulation-altium.png' },
];

const partners: Partner[] = [
  { name: 'UADY', logoSrc: '/img/iconos/uady_logo_black.png', logoClassName: 'h-24 w-auto max-w-32', color: '#15803d' },
  { name: 'Qualcomm', logoSrc: '/img/navbar/carousel/qualcomm.png', logoClassName: 'h-auto max-h-20 w-full max-w-80', color: '#3253dc' },
  { name: 'Altera', color: '#0071c5' },
  { name: 'Intel', logoSrc: '/img/navbar/carousel/intel.svg', logoClassName: 'h-24 w-auto max-w-48', color: '#0071c5' },
  { name: 'Silicon Labs', logoSrc: '/img/navbar/carousel/Silicon.png', logoClassName: 'h-24 w-auto max-w-64', color: '#0098c8' },
  { name: 'Yucatán', logoSrc: '/img/navbar/carousel/yucatan-renaissance.png', logoClassName: 'h-auto max-h-20 w-full max-w-80', color: '#a3104e' },
];

const academicCoordinators = [
  {
    nameKey: 'coordinator_castillo_name',
    facultyKey: 'faculty_engineering',
    photoSrc: '/img/laboratory/collaborators/DrCastillo.png',
    email: 'acastill@correo.uady.mx',
    researchGateUrl: 'https://www.researchgate.net/profile/Alejandro-Castillo-Atoche-2',
  },
  {
    nameKey: 'coordinator_quijano_name',
    facultyKey: 'faculty_engineering',
    photoSrc: '/img/laboratory/collaborators/DrQuijano.png',
    email: 'renan.quijano@correo.uady.mx',
    researchGateUrl: 'https://www.researchgate.net/profile/Renan-Quijano-Cetina',
  },
  {
    nameKey: 'coordinator_johan_name',
    facultyKey: 'faculty_mathematics',
    photoSrc: '/img/laboratory/collaborators/DrJohan.jpeg',
    email: 'johan.estrada@correo.uady.mx',
    researchGateUrl: 'https://www.researchgate.net/profile/Johan-Estrada-Lopez',
    googleScholarUrl: 'https://scholar.google.com/scholar?q=%22Johan+Estrada-L%C3%B3pez%22',
  },
];

const gallery = [
  {
    src: '/img/researchLines/MayanOnChip/inauguracion2025/1.jpg',
    titleKey: 'gallery_launch',
    altKey: 'gallery_launch_alt',
  },
  {
    src: '/img/researchLines/MayanOnChip/clausura2025/1.jpg',
    titleKey: 'gallery_first_cohort',
    altKey: 'gallery_first_cohort_alt',
  },
  {
    src: '/img/researchLines/MayanOnChip/clausura2026/1.jpg',
    titleKey: 'gallery_second_cohort',
    altKey: 'gallery_second_cohort_alt',
  },
];

function FocusIcon({ name }: { name: string }) {
  if (name === 'chip') {
    return (
      <svg aria-hidden="true" viewBox="0 0 48 48" fill="none" className="area-icon h-8 w-8">
        <path className="circuit-trace" d="M4 10h8v8m24 0v-8h8M4 38h8v-8m24 0v8h8M24 4v6m0 28v6M4 24h6m28 0h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="12" y="12" width="24" height="24" rx="4" stroke="currentColor" strokeWidth="2" />
        <rect x="19" y="19" width="10" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <circle className="circuit-node" cx="4" cy="10" r="1.5" fill="currentColor" />
        <circle className="circuit-node" cx="44" cy="10" r="1.5" fill="currentColor" />
        <circle className="circuit-node" cx="4" cy="38" r="1.5" fill="currentColor" />
        <circle className="circuit-node" cx="44" cy="38" r="1.5" fill="currentColor" />
      </svg>
    );
  }

  if (name === 'equipment') {
    return (
      <svg aria-hidden="true" viewBox="0 0 48 48" fill="none" className="area-icon h-8 w-8">
        <rect x="8" y="8" width="32" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <rect x="8" y="24" width="32" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M13 14h12m-12 16h8m-8 5h13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <circle className="equipment-light" cx="33" cy="14" r="2" fill="currentColor" />
        <circle className="equipment-light equipment-light-two" cx="35" cy="30" r="2" fill="currentColor" />
        <path d="M29 35h8m-4-4v8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" fill="none" className="area-icon h-8 w-8">
      <path className="graph-edge graph-edge-one" d="m13 34 9-17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path className="graph-edge graph-edge-two" d="m26 17 9 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path className="graph-edge graph-edge-three" d="M17 36h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path className="graph-edge graph-edge-four" d="m15 31 7-2 10 4" stroke="currentColor" strokeWidth="1.2" strokeDasharray="2 3" strokeLinecap="round" />
      <circle className="graph-node graph-node-top" cx="24" cy="13" r="5" fill="#0d1f35" stroke="currentColor" strokeWidth="1.8" />
      <circle className="graph-node graph-node-left" cx="12" cy="36" r="5" fill="#0d1f35" stroke="currentColor" strokeWidth="1.8" />
      <circle className="graph-node graph-node-right" cx="36" cy="36" r="5" fill="#0d1f35" stroke="currentColor" strokeWidth="1.8" />
      <circle className="graph-node graph-node-center" cx="24" cy="29" r="2.2" fill="currentColor" />
    </svg>
  );
}

function AnimatedStatCard({
  value,
  suffix,
  category,
  label,
  isActive,
}: {
  value: number;
  suffix: string;
  category: string;
  label: string;
  isActive: boolean;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const animationFrame = useRef<number | null>(null);
  const hasStarted = useRef(false);

  const animateValue = useCallback(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDisplayValue(value);
      return;
    }
    if (animationFrame.current !== null) cancelAnimationFrame(animationFrame.current);

    setDisplayValue(0);
    let startTime: number | null = null;
    const duration = 1600;
    const update = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(Math.floor(value * easedProgress));
      if (progress < 1) {
        animationFrame.current = requestAnimationFrame(update);
      } else {
        animationFrame.current = null;
      }
    };

    animationFrame.current = requestAnimationFrame(update);
  }, [value]);

  useEffect(() => {
    if (isActive) {
      animateValue();
      return;
    }

    if (animationFrame.current !== null) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
    hasStarted.current = false;
    setDisplayValue(0);
  }, [animateValue, isActive]);

  useEffect(() => () => {
    if (animationFrame.current !== null) cancelAnimationFrame(animationFrame.current);
  }, []);

  return (
    <article
      aria-label={`${category}: ${value}${suffix} ${label}`}
      className="group bg-white px-4 py-6 text-center transition-colors hover:bg-[#fffaf0] focus:bg-[#fffaf0] sm:px-6 sm:py-7"
    >
      <p aria-hidden="true" className="text-3xl font-extrabold tracking-tight text-[#0d1f35] transition-transform group-hover:-translate-y-1 sm:text-4xl">
        {displayValue}{displayValue > 0 ? suffix : ''}
      </p>
      <p className="mt-2 text-[0.6rem] font-bold uppercase leading-relaxed tracking-[0.1em] text-[#b5871b] sm:text-[0.65rem]">
        {category}
      </p>
      <p className="mt-1 text-xs font-medium leading-relaxed text-slate-600 sm:text-sm">
        {label}
      </p>
    </article>
  );
}

export default function MayanOnChipPage() {
  const { t } = useTranslation();
  const statsSectionRef = useRef<HTMLElement>(null);
  const [hasStatsEnteredViewport, setHasStatsEnteredViewport] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [activePartnerIndex, setActivePartnerIndex] = useState(0);
  const [shouldRotatePartners, setShouldRotatePartners] = useState(false);
  const [isPartnerHovered, setIsPartnerHovered] = useState(false);
  const [activeCoordinatorPhotoIndex, setActiveCoordinatorPhotoIndex] = useState(0);
  const [shouldRotateCoordinatorPhotos, setShouldRotateCoordinatorPhotos] = useState(false);
  const [isCoordinatorPhotoHovered, setIsCoordinatorPhotoHovered] = useState(false);
  const [activeEquipmentPhotoIndex, setActiveEquipmentPhotoIndex] = useState(0);
  const [isEquipmentPhotoHovered, setIsEquipmentPhotoHovered] = useState(false);

  useEffect(() => {
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => {
      const shouldAnimate = !motionPreference.matches;
      setShouldRotatePartners(shouldAnimate);
      setShouldRotateCoordinatorPhotos(shouldAnimate);
    };
    updateMotionPreference();
    motionPreference.addEventListener('change', updateMotionPreference);

    return () => motionPreference.removeEventListener('change', updateMotionPreference);
  }, []);

  useEffect(() => {
    if (!shouldRotatePartners || isPartnerHovered) return;

    const intervalId = window.setInterval(() => {
      setActivePartnerIndex(currentIndex => (currentIndex + 1) % partners.length);
    }, 2800);

    return () => window.clearInterval(intervalId);
  }, [isPartnerHovered, shouldRotatePartners]);

  const activePartner = partners[activePartnerIndex];
  const activeCoordinator = academicCoordinators[activeCoordinatorPhotoIndex];

  useEffect(() => {
    if (!shouldRotateCoordinatorPhotos || isCoordinatorPhotoHovered) return;

    const intervalId = window.setInterval(() => {
      setActiveCoordinatorPhotoIndex(currentIndex => (currentIndex + 1) % academicCoordinators.length);
    }, 3600);

    return () => window.clearInterval(intervalId);
  }, [isCoordinatorPhotoHovered, shouldRotateCoordinatorPhotos]);

  useEffect(() => {
    if (!shouldRotateCoordinatorPhotos || isEquipmentPhotoHovered) return;

    const intervalId = window.setInterval(() => {
      setActiveEquipmentPhotoIndex(currentIndex => (currentIndex + 1) % equipmentPhotos.length);
    }, 3600);

    return () => window.clearInterval(intervalId);
  }, [isEquipmentPhotoHovered, shouldRotateCoordinatorPhotos]);

  useEffect(() => {
    if (!selectedCourse) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedCourse(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCourse]);

  useEffect(() => {
    const section = statsSectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(([entry]) => {
      setHasStatsEnteredViewport(entry.isIntersecting);
    }, { threshold: 0.1 });

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#f4f7fb] text-[#10243c]">
      <style>{`
        .area-card {
          isolation: isolate;
          overflow: hidden;
          transition: transform 300ms ease, border-color 300ms ease, box-shadow 300ms ease;
        }
        .area-card:hover,
        .area-card:focus-within {
          transform: translateY(-5px);
          border-color: rgba(181, 135, 27, 0.6);
          box-shadow: 0 18px 35px rgba(13, 31, 53, 0.14);
        }
        .area-card:hover .area-icon-box,
        .area-card:focus-within .area-icon-box {
          transform: scale(1.06);
          box-shadow: 0 0 24px rgba(213, 165, 45, 0.24);
        }
        .partner-logo-enter {
          animation: partnerLogoEnter 450ms ease-out both;
        }
        .coordinator-photo-enter {
          animation: coordinatorPhotoEnter 500ms ease-out both;
        }
        @keyframes partnerLogoEnter {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes coordinatorPhotoEnter {
          from { opacity: 0.55; transform: scale(1.015); }
          to { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .coordinator-photo-enter {
            animation: none;
          }
        }
        .board-graphic {
          opacity: 0.12;
          transition: opacity 300ms ease;
        }
        .area-card:hover .board-graphic,
        .area-card:focus-within .board-graphic {
          opacity: 0.4;
        }
        .network-graphic {
          opacity: 0;
          transition: opacity 300ms ease;
        }
        .area-card:hover .network-graphic,
        .area-card:focus-within .network-graphic {
          opacity: 0.48;
        }
        .network-edge {
          opacity: 0.38;
        }
        .network-active-edge {
          stroke-dasharray: 48;
          stroke-dashoffset: 48;
          opacity: 0.85;
        }
        .area-card:hover .network-active-edge,
        .area-card:focus-within .network-active-edge {
          animation: networkBranchGrow 650ms ease-out forwards;
        }
        .area-card:hover .network-active-edge:nth-child(2),
        .area-card:focus-within .network-active-edge:nth-child(2) {
          animation-delay: 100ms;
        }
        .area-card:hover .network-active-edge:nth-child(3),
        .area-card:focus-within .network-active-edge:nth-child(3) {
          animation-delay: 200ms;
        }
        .area-card:hover .network-active-edge:nth-child(4),
        .area-card:focus-within .network-active-edge:nth-child(4) {
          animation-delay: 300ms;
        }
        .area-card:hover .network-active-edge:nth-child(5),
        .area-card:focus-within .network-active-edge:nth-child(5) {
          animation-delay: 400ms;
        }
        .area-card:hover .network-active-edge:nth-child(6),
        .area-card:focus-within .network-active-edge:nth-child(6) {
          animation-delay: 500ms;
        }
        .area-card:hover .network-active-edge:nth-child(7),
        .area-card:focus-within .network-active-edge:nth-child(7) {
          animation-delay: 600ms;
        }
        .network-node {
          opacity: 0.4;
        }
        .network-active-node {
          opacity: 0;
          transform-box: fill-box;
          transform-origin: center;
        }
        .area-card:hover .network-active-node,
        .area-card:focus-within .network-active-node {
          animation: networkNodeAppear 450ms ease-out forwards;
        }
        .area-card:hover .network-active-node:nth-child(2),
        .area-card:focus-within .network-active-node:nth-child(2) {
          animation-delay: 120ms;
        }
        .area-card:hover .network-active-node:nth-child(3),
        .area-card:focus-within .network-active-node:nth-child(3) {
          animation-delay: 240ms;
        }
        .area-card:hover .network-active-node:nth-child(4),
        .area-card:focus-within .network-active-node:nth-child(4) {
          animation-delay: 360ms;
        }
        .area-card:hover .network-active-node:nth-child(5),
        .area-card:focus-within .network-active-node:nth-child(5) {
          animation-delay: 480ms;
        }
        .area-card:hover .network-active-node:nth-child(6),
        .area-card:focus-within .network-active-node:nth-child(6) {
          animation-delay: 600ms;
        }
        .area-card:hover .network-active-node:nth-child(7),
        .area-card:focus-within .network-active-node:nth-child(7) {
          animation-delay: 720ms;
        }
        .board-trace {
          opacity: 0.36;
        }
        .board-signal {
          stroke-dasharray: 7 32;
          stroke-dashoffset: 39;
          opacity: 0;
        }
        .area-card:hover .board-signal,
        .area-card:focus-within .board-signal {
          animation: boardSignalTravel 1.4s ease-out 250ms 1 both;
        }
        .area-card:hover .board-signal-two,
        .area-card:focus-within .board-signal-two {
          animation-delay: 700ms;
        }
        .equipment-graphic {
          opacity: 0.1;
          transition: opacity 300ms ease;
        }
        .area-card:hover .equipment-graphic,
        .area-card:focus-within .equipment-graphic {
          opacity: 0.34;
        }
        .equipment-signal {
          stroke-dasharray: 8 44;
          stroke-dashoffset: 52;
          opacity: 0;
        }
        .area-card:hover .equipment-signal,
        .area-card:focus-within .equipment-signal {
          animation: equipmentSignalTravel 1.6s ease-out 250ms 1 both;
        }
        .equipment-light {
          transform-box: fill-box;
          transform-origin: center;
          opacity: 0.5;
        }
        .area-card:hover .equipment-light,
        .area-card:focus-within .equipment-light {
          animation: equipmentIndicatorPulse 1.2s ease-out 500ms 1 both;
        }
        .area-card:hover .equipment-light-two,
        .area-card:focus-within .equipment-light-two {
          animation-delay: 750ms;
        }
        .board-chip {
          opacity: 0;
          transform-box: fill-box;
          transform-origin: center;
        }
        .area-card:hover .board-chip,
        .area-card:focus-within .board-chip {
          animation: boardChipAssemble 650ms cubic-bezier(0.2, 0.8, 0.2, 1) 1.75s forwards;
        }
        .circuit-trace,
        .circuit-node,
        .graph-edge,
        .graph-node,
        .equipment-light,
        .doodle-trace,
        .doodle-node {
          animation-play-state: paused;
        }
        .area-card:hover .circuit-trace,
        .area-card:focus-within .circuit-trace,
        .area-card:hover .circuit-node,
        .area-card:focus-within .circuit-node,
        .area-card:hover .graph-edge,
        .area-card:focus-within .graph-edge,
        .area-card:hover .graph-node,
        .area-card:focus-within .graph-node,
        .area-card:hover .equipment-light,
        .area-card:focus-within .equipment-light,
        .area-card:hover .doodle-trace,
        .area-card:focus-within .doodle-trace,
        .area-card:hover .doodle-node,
        .area-card:focus-within .doodle-node {
          animation-play-state: running;
        }
        .circuit-trace {
          stroke-dasharray: 56;
          stroke-dashoffset: 56;
          opacity: 0;
        }
        .area-card:hover .circuit-trace,
        .area-card:focus-within .circuit-trace {
          animation: circuitBuild 900ms ease-out forwards;
        }
        .circuit-node {
          opacity: 0;
          transform-box: fill-box;
          transform-origin: center;
        }
        .area-card:hover .circuit-node,
        .area-card:focus-within .circuit-node {
          animation: nodeAssemble 400ms ease-out forwards;
          animation-delay: 500ms;
        }
        .graph-edge {
          stroke-dasharray: 28;
          stroke-dashoffset: 28;
        }
        .area-card:hover .graph-edge,
        .area-card:focus-within .graph-edge {
          animation: graphConnect 550ms ease-out forwards;
        }
        .area-card:hover .graph-edge-two,
        .area-card:focus-within .graph-edge-two {
          animation-delay: 120ms;
        }
        .area-card:hover .graph-edge-three,
        .area-card:focus-within .graph-edge-three {
          animation-delay: 240ms;
        }
        .area-card:hover .graph-edge-four,
        .area-card:focus-within .graph-edge-four {
          animation-delay: 360ms;
        }
        .graph-node {
          transform-box: fill-box;
          transform-origin: center;
          opacity: 0.35;
        }
        .area-card:hover .graph-node,
        .area-card:focus-within .graph-node {
          animation: graphNode 650ms cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
        }
        .area-card:hover .graph-node-left,
        .area-card:focus-within .graph-node-left {
          animation-delay: 100ms;
        }
        .area-card:hover .graph-node-right,
        .area-card:focus-within .graph-node-right {
          animation-delay: 220ms;
        }
        .area-card:hover .graph-node-center,
        .area-card:focus-within .graph-node-center {
          animation-delay: 400ms;
        }
        .doodle-trace {
          stroke-dasharray: 90;
          stroke-dashoffset: 90;
          opacity: 0;
        }
        .area-card:hover .doodle-trace,
        .area-card:focus-within .doodle-trace {
          animation: circuitBuild 1s ease-out 1.6s forwards;
        }
        .area-card:hover .doodle-trace:nth-child(2),
        .area-card:focus-within .doodle-trace:nth-child(2) {
          animation-delay: 1.75s;
        }
        .area-card:hover .doodle-trace:nth-child(3),
        .area-card:focus-within .doodle-trace:nth-child(3) {
          animation-delay: 1.9s;
        }
        .doodle-node {
          opacity: 0;
          transform-box: fill-box;
          transform-origin: center;
        }
        .area-card:hover .doodle-node,
        .area-card:focus-within .doodle-node {
          animation: nodeAssemble 400ms ease-out forwards;
          animation-delay: 2.3s;
        }
        @keyframes circuitBuild {
          0% { stroke-dashoffset: 90; opacity: 0; }
          10% { opacity: 0.8; }
          100% { stroke-dashoffset: 0; opacity: 0.55; }
        }
        @keyframes boardSignalTravel {
          0% { stroke-dashoffset: 39; opacity: 0; }
          15% { opacity: 0.85; }
          85% { opacity: 0.85; }
          100% { stroke-dashoffset: -78; opacity: 0; }
        }
        @keyframes boardChipAssemble {
          0% { opacity: 0; transform: scale(0.7); }
          70% { opacity: 0.28; transform: scale(1.04); }
          100% { opacity: 0.28; transform: scale(1); }
        }
        @keyframes equipmentSignalTravel {
          0% { stroke-dashoffset: 52; opacity: 0; }
          15% { opacity: 0.8; }
          85% { opacity: 0.8; }
          100% { stroke-dashoffset: -156; opacity: 0; }
        }
        @keyframes equipmentIndicatorPulse {
          0% { opacity: 0; transform: scale(0.7); }
          30% { opacity: 0.85; }
          100% { opacity: 0; transform: scale(1.35); }
        }
        @keyframes networkBranchGrow {
          to { stroke-dashoffset: 0; }
        }
        @keyframes networkNodeAppear {
          from { opacity: 0; transform: scale(0.4); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes nodeAssemble {
          from { opacity: 0; transform: scale(0.3); }
          to { opacity: 0.8; transform: scale(1); }
        }
        @keyframes graphConnect {
          to { stroke-dashoffset: 0; }
        }
        @keyframes graphNode {
          0% { opacity: 0.35; transform: scale(0.7); }
          70% { opacity: 1; transform: scale(1.2); }
          100% { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .partner-logo-enter {
            animation: none;
          }
          .area-card {
            transition: none;
          }
          .area-card:hover,
          .area-card:focus-within {
            transform: none;
          }
          .area-card:hover *,
          .area-card:focus-within * {
            animation: none !important;
          }
          .area-card:hover .board-graphic,
          .area-card:focus-within .board-graphic {
            opacity: 0.4;
            transition: none;
          }
          .area-card:hover .equipment-graphic,
          .area-card:focus-within .equipment-graphic {
            opacity: 0.34;
            transition: none;
          }
          .equipment-signal {
            display: none;
          }
          .area-card:hover .equipment-light,
          .area-card:focus-within .equipment-light {
            opacity: 0.5;
            transform: none;
          }
          .board-signal {
            display: none;
          }
          .area-card:hover .board-chip,
          .area-card:focus-within .board-chip {
            opacity: 0.28;
            transform: none;
          }
          .area-card:hover .network-graphic,
          .area-card:focus-within .network-graphic {
            opacity: 0.48;
            transition: none;
          }
          .area-card:hover .network-active-edge,
          .area-card:focus-within .network-active-edge {
            stroke-dashoffset: 0;
            opacity: 0.85;
          }
          .area-card:hover .network-active-node,
          .area-card:focus-within .network-active-node {
            opacity: 0.9;
            transform: none;
          }
          .area-card:hover .circuit-trace,
          .area-card:focus-within .circuit-trace,
          .area-card:hover .graph-edge,
          .area-card:focus-within .graph-edge,
          .area-card:hover .doodle-trace,
          .area-card:focus-within .doodle-trace {
            stroke-dashoffset: 0;
            opacity: 0.65;
          }
          .area-card:hover .circuit-node,
          .area-card:focus-within .circuit-node,
          .area-card:hover .graph-node,
          .area-card:focus-within .graph-node,
          .area-card:hover .doodle-node,
          .area-card:focus-within .doodle-node {
            opacity: 0.8;
          }
        }
      `}</style>
      <section className="relative isolate min-h-[calc(100vh+12rem)] overflow-hidden bg-[#0d1f35] text-white">
        <img
          src="/img/researchLines/MayanOnChip/clausura2026/1.jpg"
          alt={t('mayanOnChip.hero_image_alt')}
          className="absolute inset-0 -z-20 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#071527]/95 via-[#0d1f35]/85 to-[#0d1f35]/35" />
        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-[#071527]/70 via-transparent to-transparent" />

        <div className="mx-auto flex min-h-[calc(100vh+12rem)] max-w-7xl items-center px-6 py-20 lg:px-12">
          <div className="max-w-3xl">
            <span className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d9aa38]/50 bg-[#d9aa38]/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#f0c75e]">
              <span className="h-2 w-2 rounded-full bg-[#f0c75e]" />
              {t('mayanOnChip.hero_eyebrow')}
            </span>
            <h1 className="text-5xl font-extrabold leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
              Mayan-On-Chip
              <span className="mt-3 block text-2xl font-semibold leading-snug text-white/80 sm:text-3xl lg:text-4xl">
                {t('mayanOnChip.hero_title')}
              </span>
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-8 text-white/80 sm:text-lg">
              {t('mayanOnChip.hero_description')}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href="https://mayanonchip.industria4fiuady.com/login/index.php"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 rounded-xl bg-[#d5a52d] px-6 py-3.5 text-sm font-bold text-[#10243c] shadow-lg shadow-black/20 transition hover:-translate-y-0.5 hover:bg-[#e3b842] focus:outline-none focus:ring-2 focus:ring-white"
              >
                {t('mayanOnChip.platform_cta')}
                <span aria-hidden="true">↗</span>
              </a>
              <a
                href="#program"
                className="inline-flex items-center gap-3 rounded-xl border border-white/30 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
              >
                {t('mayanOnChip.discover_cta')}
                <span aria-hidden="true">↓</span>
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 right-0 hidden items-center gap-3 bg-white/10 px-8 py-4 text-xs font-medium uppercase tracking-[0.16em] text-white/75 backdrop-blur-md md:flex">
          <span className="h-px w-10 bg-[#d5a52d]" />
          {t('mayanOnChip.location')}
        </div>
      </section>

      <section
        ref={statsSectionRef}
        aria-label={t('mayanOnChip.impact_title')}
        className="relative z-10 mx-auto -mt-8 grid max-w-6xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 shadow-xl lg:grid-cols-4"
      >
        {programStats.map(stat => (
          <AnimatedStatCard
            key={stat.key}
            value={stat.value}
            suffix={stat.suffix}
            category={t(`mayanOnChip.${stat.categoryKey}`)}
            label={t(`mayanOnChip.stat_${stat.key}_label`)}
            isActive={hasStatsEnteredViewport}
          />
        ))}
      </section>
      <section id="program" className="mx-auto flex max-w-7xl scroll-mt-8 flex-col gap-12 px-6 py-24 lg:px-12">
        <div className="order-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#b5871b]">
            {t('mayanOnChip.about_eyebrow')}
          </p>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight text-[#0d1f35] sm:text-4xl">
            {t('mayanOnChip.about_title')}
          </h2>
          <p className="mt-6 text-base leading-8 text-slate-600">
            {t('mayanOnChip.about_description')}
          </p>
          <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,18rem)] lg:items-stretch">
            <div className="space-y-5 border-l-2 border-[#d5a52d] pl-5">
              <h3 className="font-bold text-[#0d1f35]">{t('mayanOnChip.coordinator_name')}</h3>
              {academicCoordinators.map(({ nameKey, facultyKey, email, researchGateUrl, googleScholarUrl }) => (
                <div key={nameKey}>
                  <p className="font-semibold text-[#0d1f35]">{t(`mayanOnChip.${nameKey}`)}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-500">
                    {t(`mayanOnChip.${facultyKey}`)} · {t('mayanOnChip.coordinator_lab')}
                  </p>
                  <a
                    href={`mailto:${email}`}
                    className="mt-1 inline-block text-sm text-[#315b7d] underline decoration-slate-300 underline-offset-2 transition hover:text-[#b5871b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d5a52d]"
                  >
                    {email}
                  </a>
                  {researchGateUrl && (
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <a
                        href={researchGateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#315b7d] underline decoration-slate-300 underline-offset-2 transition hover:text-[#b5871b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d5a52d]"
                      >
                        ResearchGate
                      </a>
                      {googleScholarUrl && (
                        <a
                          href={googleScholarUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#315b7d] underline decoration-slate-300 underline-offset-2 transition hover:text-[#b5871b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d5a52d]"
                        >
                          Google Scholar
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <figure
              aria-roledescription={t('mayanOnChip.coordinator_photo_carousel')}
              aria-label={t('mayanOnChip.coordinator_name')}
              className="relative aspect-[4/5] min-h-0 w-full max-w-72 justify-self-center overflow-hidden rounded-2xl border border-slate-200 bg-slate-200 shadow-md"
              onMouseEnter={() => setIsCoordinatorPhotoHovered(true)}
              onMouseLeave={() => setIsCoordinatorPhotoHovered(false)}
              onFocus={() => setIsCoordinatorPhotoHovered(true)}
              onBlur={event => {
                if (!event.currentTarget.contains(event.relatedTarget)) setIsCoordinatorPhotoHovered(false);
              }}
            >
              <img
                key={activeCoordinator.nameKey}
                src={activeCoordinator.photoSrc}
                alt={t(`mayanOnChip.${activeCoordinator.nameKey}`)}
                className="coordinator-photo-enter absolute inset-0 h-full w-full object-cover object-top"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#071527]/90 via-[#071527]/50 to-transparent px-5 pb-14 pt-12 text-sm font-semibold text-white">
                {t(`mayanOnChip.${activeCoordinator.nameKey}`)}
              </figcaption>
              <div className="absolute inset-x-0 bottom-4 flex justify-center gap-2">
                {academicCoordinators.map(({ nameKey }, index) => (
                  <button
                    key={nameKey}
                    type="button"
                    aria-label={`${t('mayanOnChip.coordinator_show_photo_of')} ${t(`mayanOnChip.${nameKey}`)}`}
                    aria-pressed={index === activeCoordinatorPhotoIndex}
                    onClick={() => setActiveCoordinatorPhotoIndex(index)}
                    className={`h-2.5 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0d1f35] ${
                      index === activeCoordinatorPhotoIndex ? 'w-7 bg-[#f0c75e]' : 'w-2.5 bg-white/75 hover:bg-white'
                    }`}
                  />
                ))}
              </div>
            </figure>
          </div>
        </div>

        <div className="order-1">
          <div className="grid items-stretch gap-4 sm:grid-cols-2">
            {focusAreas.map(({ key, icon }) => (
              <article
                key={key}
                className={`area-card relative flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6 ${
                  key === 'laboratory' ? 'sm:col-span-2' : ''
                }`}
              >
                {icon === 'chip' ? (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 300 440"
                    preserveAspectRatio="xMidYMid meet"
                    fill="none"
                    className="board-graphic pointer-events-none absolute bottom-0 right-0 z-[1] h-full w-auto max-w-full text-[#b5871b]"
                  >
                    <g fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path className="board-trace" d="M82 76v18q0 6-6 6H14q-8 0-8 8v76" />
                      <path className="board-trace" d="M6 126h10v24H6" />
                      <path className="board-trace" d="M6 148h15v18H6" />
                      <path className="board-trace" d="M6 214v62q0 8 8 8h7" />
                      <path className="board-trace" d="M218 80h70q8 0 8 8v40q0 8 4 8" />
                      <path className="board-trace" d="M296 108h-14v20h14" />
                      <path className="board-trace" d="M296 170v62q0 8-8 8h-2" />
                      <path className="board-trace" d="M298 260v42q0 8-8 8h-2" />
                      <path className="board-trace" d="M6 324v42q0 8 8 8h24" />
                      <path className="board-trace" d="M298 328v50q0 8-8 8h-16v19h-4" />
                      <path className="board-trace" d="M82 76v13q0 6 6 6h32" />
                      <path className="board-trace" d="M218 80V96q0 6-6 6h-35" />
                      <path className="board-trace" d="M21 284v16q0 6-6 6H6" />
                      <path className="board-trace" d="M288 310h-8q-6 0-6 6v12" />
                    </g>
                    <g fill="none" stroke="#f0c75e" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                      <path className="board-signal" d="M82 76v18q0 6-6 6H14q-8 0-8 8v76" />
                      <path className="board-signal board-signal-two" d="M6 324v42q0 8 8 8h24" />
                    </g>
                    <g fill="white" stroke="currentColor" strokeWidth="1.5">
                      <circle cx="6" cy="188" r="3" />
                      <circle cx="6" cy="214" r="3" />
                      <circle cx="21" cy="284" r="3" />
                      <circle cx="21" cy="166" r="3" />
                      <circle cx="298" cy="136" r="3" />
                      <circle cx="282" cy="128" r="3" />
                      <circle cx="286" cy="240" r="3" />
                      <circle cx="288" cy="310" r="3" />
                      <circle cx="6" cy="324" r="3" />
                      <circle cx="298" cy="328" r="3" />
                      <rect x="13" y="123" width="6" height="6" rx="1" />
                      <rect x="270" y="323" width="6" height="6" rx="1" />
                    </g>
                    <g className="board-chip">
                      <rect x="224" y="386" width="50" height="50" rx="5" fill="white" stroke="currentColor" strokeWidth="2" />
                      <rect x="229" y="391" width="40" height="40" rx="3" fill="#0d1f35" stroke="currentColor" strokeWidth="1" />
                      <text x="249" y="415" fill="#f0c75e" fontSize="7" fontWeight="800" textAnchor="middle" letterSpacing=".4">FPGA</text>
                    </g>
                  </svg>
                ) : icon === 'network' ? (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 300 440"
                    preserveAspectRatio="xMidYMid meet"
                    fill="none"
                    className="network-graphic pointer-events-none absolute bottom-0 right-0 z-[1] h-full w-auto max-w-full text-[#b5871b]"
                  >
                    <g className="network-edge" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
                      <path d="M18 92 70 72 119 106 164 66 212 98 268 72" />
                      <path d="m18 92 22 88 50-34 29-40 14 89 31-129 48 32 27 79 29-105" />
                      <path d="m40 180 42 58 8-92 50 57 25 69 47-174 27 105-10 86" />
                      <path d="m82 238 43 61 15-96 56 12 24 55 62-1" />
                      <path d="m125 299 17-96 16 108 38-96 32 59 39-59" />
                      <path d="m142 311 38 58 16-96 47-3 24 72" />
                      <path d="m18 348 44-50 63 1 55 68 48-87 39 72" />
                      <path d="m18 92 52-20m44 234 28-80 38-226" />
                    </g>
                    <g fill="white" stroke="currentColor" strokeWidth="1.4">
                      <circle className="network-node" cx="18" cy="92" r="3" />
                      <circle className="network-node" cx="70" cy="72" r="3" />
                      <circle className="network-node" cx="119" cy="106" r="3" />
                      <circle className="network-node" cx="164" cy="66" r="3" />
                      <circle className="network-node" cx="212" cy="98" r="3" />
                      <circle className="network-node" cx="268" cy="72" r="3" />
                      <circle className="network-node" cx="40" cy="180" r="3" />
                      <circle className="network-node" cx="90" cy="146" r="3" />
                      <circle className="network-node" cx="133" cy="203" r="3" />
                      <circle className="network-node" cx="164" cy="74" r="3" />
                      <circle className="network-node" cx="212" cy="98" r="3" />
                      <circle className="network-node" cx="239" cy="203" r="3" />
                      <circle className="network-node" cx="82" cy="238" r="3" />
                      <circle className="network-node" cx="125" cy="299" r="3" />
                      <circle className="network-node" cx="142" cy="311" r="3" />
                      <circle className="network-node" cx="180" cy="369" r="3" />
                      <circle className="network-node" cx="243" cy="282" r="3" />
                      <circle className="network-node" cx="282" cy="354" r="3" />
                      <circle className="network-node" cx="18" cy="348" r="3" />
                      <circle className="network-node" cx="62" cy="298" r="3" />
                      <circle className="network-node" cx="196" cy="273" r="3" />
                      <circle className="network-node" cx="268" cy="263" r="3" />
                    </g>
                    <g fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                      <path className="network-active-edge" d="M40 180 90 146" />
                      <path className="network-active-edge" d="M90 146 133 203" />
                      <path className="network-active-edge" d="M133 203 82 238" />
                      <path className="network-active-edge" d="M82 238 125 299" />
                      <path className="network-active-edge" d="M125 299 180 369" />
                      <path className="network-active-edge" d="M180 369 196 273" />
                      <path className="network-active-edge" d="M196 273 243 282" />
                      <path className="network-active-edge" d="M243 282 268 263" />
                    </g>
                    <g fill="currentColor">
                      <circle className="network-active-node" cx="90" cy="146" r="5" />
                      <circle className="network-active-node" cx="133" cy="203" r="6" />
                      <circle className="network-active-node" cx="82" cy="238" r="5" />
                      <circle className="network-active-node" cx="125" cy="299" r="5" />
                      <circle className="network-active-node" cx="180" cy="369" r="6" />
                      <circle className="network-active-node" cx="196" cy="273" r="5" />
                      <circle className="network-active-node" cx="243" cy="282" r="5" />
                      <circle className="network-active-node" cx="268" cy="263" r="5" />
                    </g>
                  </svg>
                ) : icon === 'equipment' ? (
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 300 440"
                    preserveAspectRatio="xMidYMid meet"
                    fill="none"
                    className="equipment-graphic pointer-events-none absolute bottom-0 right-0 z-[1] h-full w-auto max-w-full text-[#b5871b]"
                  >
                    <g stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M174 270v128m104-128v128M160 398h132" strokeWidth="2" />
                      <path d="M184 280h88v90h-88zM194 370v18m68-18v18" strokeWidth="1.6" />
                      <path d="M194 351h68M205 351v-25l16-13 16 13v25m16-25v25" strokeWidth="1.5" />
                      <path d="M216 300h24m-24 9h24" strokeWidth="1.4" />
                      <path d="M24 346h88v60H24zM32 356h72v40H32z" strokeWidth="1.5" />
                      <path d="M40 367h18v18H40zm35 0h18v18H75z" strokeWidth="1.4" />
                      <path d="M2 376h22m88 0h28v-24h26m-56 48h30v-18h26" strokeWidth="1.4" />
                      <path d="M170 418h122M14 422h130" strokeWidth="1" strokeDasharray="2 7" />
                    </g>
                    <path
                      className="equipment-signal"
                      d="M2 376h44v-20h42v20h44v-24h34v-82h104v88h-58v40h78"
                      stroke="#f0c75e"
                      strokeWidth="2.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle className="equipment-light" cx="262" cy="300" r="3" fill="#f0c75e" />
                    <circle className="equipment-light equipment-light-two" cx="92" cy="361" r="3" fill="#f0c75e" />
                  </svg>
                ) : (
                  <svg aria-hidden="true" viewBox="0 0 240 190" fill="none" className="pointer-events-none absolute -bottom-3 -right-3 z-[1] h-48 w-56 text-[#b5871b]/80 sm:h-56 sm:w-64">
                    <path className="doodle-trace" d="M122 188v-38h34v-30h34V84h48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path className="doodle-trace" d="M164 188v-26h28v-34h42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path className="doodle-trace" d="M88 188v-24H54v-36H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path className="doodle-trace" d="M206 188v-18h18v-26h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <circle className="doodle-node" cx="122" cy="150" r="4" fill="currentColor" />
                    <circle className="doodle-node" cx="190" cy="120" r="4" fill="currentColor" />
                    <circle className="doodle-node" cx="54" cy="128" r="4" fill="currentColor" />
                    <circle className="doodle-node" cx="224" cy="144" r="4" fill="currentColor" />
                  </svg>
                )}
                <div className={`area-content relative z-10 ${
                  key === 'equipment' || key === 'integrated_circuits'
                    ? 'grid grid-cols-[minmax(0,1fr)_6rem] items-start gap-x-3 gap-y-2 sm:grid-cols-[minmax(0,1fr)_7rem]'
                    : ''
                }`}>
                  <div className={key === 'equipment' || key === 'integrated_circuits' ? 'col-span-2' : ''}>
                    <div className="area-icon-box flex h-14 w-14 items-center justify-center rounded-xl bg-[#0d1f35] text-[#f0c75e] transition duration-300">
                      <FocusIcon name={icon} />
                    </div>
                    <h3 className="mt-5 text-lg font-bold text-[#0d1f35]">
                      {t(`mayanOnChip.area_${key}_title`)}
                    </h3>
                  </div>
                  {key === 'equipment' ? (
                    <figure
                      aria-roledescription={t('mayanOnChip.area_equipment_photo_carousel')}
                      aria-label={t('mayanOnChip.area_equipment_title')}
                      className="relative col-start-2 row-start-2 h-24 w-24 justify-self-end sm:h-28 sm:w-28"
                      onMouseEnter={() => setIsEquipmentPhotoHovered(true)}
                      onMouseLeave={() => setIsEquipmentPhotoHovered(false)}
                      onFocus={() => setIsEquipmentPhotoHovered(true)}
                      onBlur={event => {
                        if (!event.currentTarget.contains(event.relatedTarget)) setIsEquipmentPhotoHovered(false);
                      }}
                    >
                      <img
                        key={activeEquipmentPhotoIndex}
                        src={equipmentPhotos[activeEquipmentPhotoIndex].src}
                        alt={t(`mayanOnChip.${equipmentPhotos[activeEquipmentPhotoIndex].altKey}`)}
                        className="coordinator-photo-enter absolute inset-0 h-full w-full object-contain"
                      />
                      <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1">
                        {equipmentPhotos.map(({ altKey }, index) => (
                          <button
                            key={altKey}
                            type="button"
                            aria-label={`${t('mayanOnChip.area_equipment_show_photo_of')} ${t(`mayanOnChip.${altKey}`)}`}
                            aria-pressed={index === activeEquipmentPhotoIndex}
                            onClick={() => setActiveEquipmentPhotoIndex(index)}
                            className={`h-2 rounded-full transition-all focus:outline-none focus:ring-2 focus:ring-[#0d1f35] focus:ring-offset-1 ${
                              index === activeEquipmentPhotoIndex ? 'w-5 bg-[#d5a52d]' : 'w-2 bg-slate-400 hover:bg-slate-600'
                            }`}
                          />
                        ))}
                      </div>
                    </figure>
                  ) : key === 'integrated_circuits' ? (
                    <img
                      src="/img/researchLines/MayanOnChip/capabilities/integrated-circuit.png"
                      alt={t(`mayanOnChip.area_${key}_image_alt`)}
                      loading="lazy"
                      className="col-start-2 row-start-2 mt-3 h-28 w-28 justify-self-end object-contain sm:h-32 sm:w-32"
                    />
                  ) : null}
                  <p className={`area-description mt-3 text-sm leading-6 text-slate-500 ${
                    key === 'equipment' || key === 'integrated_circuits' ? 'col-start-1 row-start-2' : ''
                  }`}>
                    {t(`mayanOnChip.area_${key}_description`)}
                  </p>
                </div>
              </article>
            ))}
          </div>
          <div
            className="mt-6 rounded-2xl bg-[#e9eef5] p-6 sm:p-8"
            onMouseEnter={() => setIsPartnerHovered(true)}
            onMouseLeave={() => setIsPartnerHovered(false)}
          >
            <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-[#0d1f35]">
              {t('mayanOnChip.partners_title')}
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">{t('mayanOnChip.partners_description')}</p>
            <ul className="mt-5 flex flex-wrap gap-2">
              {partners.map((partner, index) => {
                const isActive = index === activePartnerIndex;
                return (
                  <li key={partner.name}>
                    <button
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => setActivePartnerIndex(index)}
                      className="rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors duration-300"
                      style={isActive
                        ? { borderColor: partner.color, backgroundColor: partner.color, color: '#fff' }
                        : { borderColor: '#cbd5e1', backgroundColor: '#fff', color: '#334155' }}
                    >
                      {partner.name}
                    </button>
                  </li>
                );
              })}
            </ul>
            <div
              role="img"
              aria-label={`${activePartner.name}${activePartner.logoSrc ? ' logo' : ''}`}
              className="mt-5 flex min-h-28 items-center justify-center px-6 py-4"
            >
              {activePartner.logoSrc ? (
                <div
                  key={activePartner.name}
                  className="partner-logo-enter flex min-h-24 w-full items-center justify-center"
                >
                  <img
                    src={activePartner.logoSrc}
                    alt=""
                    className={`object-contain ${activePartner.logoClassName ?? 'h-24 w-auto max-w-48'}`}
                  />
                </div>
              ) : (
                <span
                  key={activePartner.name}
                  className="partner-logo-enter text-2xl font-bold tracking-tight"
                  style={{ color: activePartner.color }}
                >
                  {activePartner.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#0d1f35] py-20 text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[0.8fr_1.2fr] lg:items-center lg:px-12">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#f0c75e]">
              {t('mayanOnChip.training_eyebrow')}
            </p>
            <h2 className="mt-4 text-3xl font-extrabold leading-tight sm:text-4xl">
              {t('mayanOnChip.training_title')}
            </h2>
            <p className="mt-5 text-base leading-8 text-white/70">{t('mayanOnChip.training_description')}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {courses.map(({ key, image }) => (
              <article key={key} className="overflow-hidden rounded-2xl border border-white/15 bg-white/[0.06] shadow-lg backdrop-blur">
                <button
                  type="button"
                  onClick={() => setSelectedCourse({ key, image })}
                  aria-label={t('mayanOnChip.course_view_larger')}
                  className="group/image block w-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#f0c75e]"
                >
                  <img
                    src={`/img/researchLines/MayanOnChip/courses/${image}`}
                    alt={t(`mayanOnChip.course_${key}_alt`)}
                    loading="lazy"
                    className="aspect-[1.2/1] w-full object-cover transition duration-300 group-hover/image:scale-[1.03]"
                  />
                  <span className="sr-only">
                    {t('mayanOnChip.course_view_larger')}
                  </span>
                </button>
                <div className="p-5">
                  <span className="text-[0.65rem] font-bold uppercase tracking-[0.16em] text-[#f0c75e]">
                    {t('mayanOnChip.course_label')}
                  </span>
                  <h3 className="mt-2 text-lg font-bold leading-snug text-white">
                    {t(`mayanOnChip.course_${key}_title`)}
                  </h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 lg:px-12">
        <div className="mb-10 flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#b5871b]">
              {t('mayanOnChip.gallery_eyebrow')}
            </p>
            <h2 className="mt-3 text-3xl font-extrabold text-[#0d1f35] sm:text-4xl">
              {t('mayanOnChip.gallery_title')}
            </h2>
          </div>
          <p className="max-w-lg text-sm leading-6 text-slate-500">{t('mayanOnChip.gallery_description')}</p>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {gallery.map(({ src, titleKey, altKey }, index) => (
            <figure key={src} className={`group relative overflow-hidden rounded-2xl bg-slate-200 shadow-md ${index === 0 ? "md:col-span-2" : ""}`}>
              <img
                src={src}
                alt={t(`mayanOnChip.${altKey}`)}
                loading="lazy"
                className="h-72 w-full object-cover transition duration-700 group-hover:scale-105 md:h-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#071527]/85 via-transparent to-transparent" />
              <figcaption className="absolute bottom-0 left-0 p-5 text-base font-semibold text-white sm:p-6">
                {t(`mayanOnChip.${titleKey}`)}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 rounded-3xl bg-[#d5a52d] p-8 text-[#10243c] sm:p-12 lg:flex-row lg:items-center">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3f351c]">
              {t('mayanOnChip.cta_eyebrow')}
            </p>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight sm:text-4xl">
              {t('mayanOnChip.cta_title')}
            </h2>
            <p className="mt-4 text-sm leading-7 text-[#26364a]">{t('mayanOnChip.cta_description')}</p>
          </div>
          <a
            href="https://mayanonchip.industria4fiuady.com/login/index.php"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-3 rounded-xl bg-[#0d1f35] px-6 py-4 text-sm font-bold text-white transition hover:bg-[#183656] focus:outline-none focus:ring-2 focus:ring-[#0d1f35] focus:ring-offset-2 focus:ring-offset-[#d5a52d]"
          >
            {t('mayanOnChip.platform_cta')}
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </section>

      {selectedCourse && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-[#071527]/90 p-4 backdrop-blur-sm sm:p-8"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setSelectedCourse(null);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t(`mayanOnChip.course_${selectedCourse.key}_title`)}
            className="relative flex max-h-full max-w-5xl flex-col items-center"
          >
            <button
              type="button"
              autoFocus
              onClick={() => setSelectedCourse(null)}
              aria-label={t('mayanOnChip.course_close_preview')}
              className="absolute -right-2 -top-2 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-2xl font-bold text-[#10243c] shadow-lg transition hover:bg-[#f0c75e] focus:outline-none focus:ring-2 focus:ring-white sm:-right-5 sm:-top-5"
            >
              <span aria-hidden="true">&times;</span>
            </button>
            <img
              src={`/img/researchLines/MayanOnChip/courses/${selectedCourse.image}`}
              alt={t(`mayanOnChip.course_${selectedCourse.key}_alt`)}
              className="max-h-[calc(100vh-3rem)] max-w-full rounded-lg object-contain shadow-2xl sm:max-h-[calc(100vh-5rem)]"
            />
          </div>
        </div>
      )}
    </div>
  );
}
